import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import Alert from 'reactstrap/lib/Alert';

import { receiveProducts, receiveTickets } from '@ttstr/actions';
import { Container, LoadingSpinner, Masonry, useIntl } from '@ttstr/components';
import { AppState } from '@ttstr/reducers';
import { useActions, useShallowEqualSelector } from '@ttstr/utils';
import { Article } from '@ttstr/api';
import FilteredMasonry from './FilteredMasonry';

// wird nicht mehr gebraucht: Header soll ganz raus
// import MerchstandImages from './assets/images/MerchstandImages';

interface RouteParams {
  readonly id: string;
}

type Props = {
  /**
   * productFilter can be used if you want a Route to a dedicated page for special merch/products.
   *
   * Example:
   * You want a page only with merch that has the tag id 1234.
   * The function for productFilter would then be
   *  p => p.tag_ids.includes(1234)
   *
   * Then create a route in App.tsx with
   * <Route exact path="/specialmerch" render={()=> <ProductListing productFilter={p => p.tag_ids.includes(1234)} />} />
   */
  productFilter?: (p: Article) => any;
};

const Home: React.FC<Props> = ({ productFilter = (p) => p }) => {
  const { t } = useTranslation();
  const params = useParams<RouteParams>();
  const { products, loadingProducts, tickets, loadingTickets } = useShallowEqualSelector(mapStateToProps);
  const { receiveProducts, receiveTickets } = useActions(mapDispatchToProps);
  const { language } = useIntl();

  if (!productFilter && params?.id) {
    productFilter = (p) => p.category_ids.includes(Number(params.id));
  }

  React.useEffect(() => {
    receiveProducts();
    receiveTickets();
  }, [language]);

  return (
    <article className="mb-5">
      <Helmet>
        <title>{t(`PRODUCTS.TITLE`)}</title>
        <body className="landing-page" />
      </Helmet>
      <Container fluid className="hero d-flex justify-content-center align-items-center flex-column">
        <div className="hero-wrapper p-5">
          <div className="hero-content d-flex justify-content-center align-items-center flex-column">
            <h1 className="mb-3">
              Fettes Brot <span className="cream">Brotstock</span>
              <br />
              <small>
                Die letzte Show auf der Welt
                <br />
                Hamburg 01.-02.09.2023
              </small>
            </h1>
            {/* <img src={require('./assets/images/fb_tape_bs.png')} className="img-fluid mt-3 header-image" alt="" /> */}
            <span className="arrow">
              <i className="fa fa-arrow-down"></i>
            </span>
            <a href="https://fettesbrotshop.de/faq">
              <img src={require('./assets/images/faq.png')} className="badge-faq d-none" alt="" />
            </a>
          </div>
        </div>
      </Container>
      <div className="list-page-highlights pt-5">
        <Container fluid className="pb-5 mt-5 mt-sm-3">
          {/* <h1 className="mb-5 mt-2 pb-3">
                Jetzt <span className="cream">bestellen</span>
              </h1> */}
          {loadingProducts || loadingTickets ? ( // if loading
            <LoadingSpinner label={t(`LOADING.PRODUCTS`)} />
          ) : products.length === 0 ? ( // if no products
            <Alert color="info">{t(`MASONRY.NO_ITEMS`)}</Alert>
          ) : (
            // else show products
            <FilteredMasonry
              id="product-listing"
              products={products.filter(productFilter)}
              filters="off"
              dontUpdateQueryStrings
              tagId={9242}
              showSubtitle
              columnClassName="col-12 col-md-4 col-lg-4 mb-0"
              // itemClassName="random-rotation-object"
              // itemImageClassName="scribble-hover-overlay-object"
            />
          )}
        </Container>
      </div>
    </article>
  );
};

const mapStateToProps = (state: AppState) => {
  const { products } = state.Products;
  const loadingProducts = state.Products.loading;
  const { tickets } = state.Tickets;
  const loadingTickets = state.Tickets.loading;
  return {
    products,
    loadingProducts,
    tickets,
    loadingTickets,
  };
};

const mapDispatchToProps = {
  receiveProducts,
  receiveTickets,
};

export default React.memo(Home);
